import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import {
  buildLinkedInUrl,
  buildXUrl,
  CDN_URL,
  getInlineLoader,
} from "../Helpers";
import PageHero from "../ones/PageHero";
import Footer from "../ones/Footer";
import { FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import CallToAction from "../ones/callToAction";

export default function OurTeamLanding() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function init() {
      setReady(false);
      await appContext.getAllTeamMembers();
      if (mounted) {
        setReady(true);
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, []);

  const teamMembers = useMemo(() => {
    if (!Array.isArray(appContext.allTeamMembers)) {
      return [];
    }

    return appContext.allTeamMembers;
  }, [appContext.allTeamMembers]);

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <>
      <PageHero
        title="Meet the people shaping AGS field delivery and geospatial strategy"
        description="Each leadership badge flips to reveal a short profile. Team records and social usernames are now loaded directly from the database."
        badge="Our Team"
      />

      <section className="ags-section">
        <div className="container">
          {teamMembers.length > 0 ? (
            <div className="row g-4">
              {teamMembers.map((member, index) => {
                const linkedInUrl = buildLinkedInUrl(member.linkedIn);
                const xUrl = buildXUrl(member.twitter);

                return (
                  <div
                    className="col-md-6 col-xl-4"
                    key={member.uuid || member.fullname || index}
                    data-aos="flip-left"
                    data-aos-delay={index * 60}
                  >
                    <div className="ags-team-card">
                      <div className="ags-team-card-inner">
                        <div className="ags-team-card-face ags-team-card-front">
                          <div className="ags-team-image-wrap">
                            <img
                              src={`${CDN_URL}${member.photo}`}
                              alt={member.fullname}
                              className="ags-team-image"
                            />
                          </div>
                          <div className="ags-team-card-body">
                            <h4 className="font-bold mb-1">{member.fullname}</h4>
                            <p className="text-muted mb-3">{member.position}</p>
                            <div className="d-flex gap-2 justify-content-center">
                              {linkedInUrl ? (
                                <a
                                  href={linkedInUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ags-social-btn"
                                  aria-label={`${member.fullname} LinkedIn`}
                                >
                                  <FaLinkedinIn />
                                </a>
                              ) : null}
                              {xUrl ? (
                                <a
                                  href={xUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ags-social-btn"
                                  aria-label={`${member.fullname} X`}
                                >
                                  <FaXTwitter />
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="ags-team-card-face ags-team-card-back">
                          <div className="ags-team-back-thumb">
                            <img src={`${CDN_URL}${member.photo}`} alt={member.fullname} />
                          </div>
                          <h5 className="font-bold text-white mb-2">
                            {member.fullname}
                          </h5>
                          <p className="text-white-50 mb-3">{member.description}</p>
                          <p className="mNoMargin text-white-50 ">
                            <span className="font-semi-bold text-white">Role:</span>{" "}
                            {member.position}
                          </p>

                          <div className="d-flex gap-2 justify-content-center mt-4">
                              {linkedInUrl ? (
                                <a
                                  href={linkedInUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ags-social-btn bg-light"
                                  aria-label={`${member.fullname} LinkedIn`}
                                >
                                  <FaLinkedinIn />
                                </a>
                              ) : null}
                              {xUrl ? (
                                <a
                                  href={xUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ags-social-btn text-light"
                                  aria-label={`${member.fullname} X`}
                                >
                                  <FaXTwitter />
                                </a>
                              ) : null}
                            </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ags-empty-state text-center">
              <p className="ags-section-kicker mb-2">Our Team</p>
              <h3 className="font-bold mb-3">Team profiles are coming soon</h3>
              <p className="text-muted mb-0">
                Member information will appear here once records are available.
              </p>
            </div>
          )}
        </div>
      </section>

      <CallToAction />

      <Footer />
    </>
  );
}
